/**
 * mutationHelpers — Phase 3.2 (Plan §8) shared helpers for *Mutations.ts.
 *
 * The dozen `*Mutations.ts` files in this package share three lookup /
 * immutable-update patterns that account for ~25% of their LOC:
 *
 *   1. `drawingDocument.blocks[blockName]` + `if (!blockDef) throw`
 *   2. `{ ...drawingDocument.blocks, [blockName]: { ...blockDef, ... } }`
 *   3. `drawingDocument.spaces.map(space => ({ ...space, entities:
 *      space.entities.map(entity => ...) }))` with the inner `entity` walk
 *      filtering on `entity.type === "Block" && entity.blockName === ...`
 *
 * This module centralises the three patterns. Each helper preserves the
 * exact semantics of the inlined code it replaces (no behaviour change).
 * Counter-tracking helpers return both the updated document and the
 * count of affected entries so callers can build user-facing summaries
 * without re-walking the document.
 */

import type {
  BlockDefinition,
  DrawingDocument,
  DrawingEntity,
  DrawingSpace,
} from "@dxf-ai-editor/drawing-model";

/** A `DrawingEntity` narrowed to the `Block` insert variant. */
export type BlockInsertEntity = Extract<DrawingEntity, { type: "Block" }>;

/**
 * Lookup a block by name; throw with a consistent error if missing.
 * Replaces the repeated `const blockDef = drawingDocument.blocks[name];
 * if (!blockDef) throw new Error(\`Unknown block: \${name}\`);` block.
 */
export function requireBlock(
  doc: DrawingDocument,
  blockName: string,
): BlockDefinition {
  const blockDef = doc.blocks[blockName];
  if (!blockDef) {
    throw new Error(`Unknown block: ${blockName}`);
  }
  return blockDef;
}

/**
 * Replace a block definition immutably and return the updated document.
 * Caller passes a `mutator` that receives the existing `blockDef` and
 * returns the next one (typically `{ ...blockDef, entities: nextEntities }`).
 */
export function withBlockReplace(
  doc: DrawingDocument,
  blockName: string,
  mutator: (blockDef: BlockDefinition) => BlockDefinition,
): DrawingDocument {
  const blockDef = requireBlock(doc, blockName);
  return {
    ...doc,
    blocks: {
      ...doc.blocks,
      [blockName]: mutator(blockDef),
    },
  };
}

/**
 * Walk every entity in every space and apply a mapper. Returns the
 * updated document plus a count of entries the mapper *changed*
 * (detected by referential identity — mapper returning the same object
 * means "no change").
 */
export function withSpaceEntities(
  doc: DrawingDocument,
  mapper: (entity: DrawingEntity, space: DrawingSpace) => DrawingEntity,
): { doc: DrawingDocument; changedCount: number } {
  let changedCount = 0;
  const nextSpaces = doc.spaces.map((space) => ({
    ...space,
    entities: space.entities.map((entity) => {
      const next = mapper(entity, space);
      if (next !== entity) changedCount += 1;
      return next;
    }),
  }));
  return { doc: { ...doc, spaces: nextSpaces }, changedCount };
}

/**
 * Walk every `Block`-type entity whose `blockName` matches and apply a
 * mapper. Non-matching entities pass through unchanged. Returns the
 * updated document + count of insert entries the mapper changed.
 *
 * Replaces the very common per-Insert update pattern in
 * battmanMutations.ts, attsyncMutation.ts, blockMutations.ts, etc.
 */
export function withInsertsOfBlock(
  doc: DrawingDocument,
  blockName: string,
  mapper: (insert: BlockInsertEntity) => BlockInsertEntity,
): { doc: DrawingDocument; changedCount: number } {
  return withSpaceEntities(doc, (entity) => {
    if (entity.type !== "Block" || entity.blockName !== blockName) {
      return entity;
    }
    return mapper(entity);
  });
}
